import React from 'react';
import CTA from '../components/CTA';
import MiniContactForm from '../components/MiniContactForm';
import FAQ from '../components/FAQ';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';

const SOLAR_PANEL_FAQ = [
  {
    question: 'How often should I have my solar panels cleaned?',
    answer: 'We recommend cleaning your solar panels every 6-12 months to maintain optimal efficiency. In dusty areas or after heavy storms, more frequent cleaning may be necessary.'
  },
  {
    question: 'Is your cleaning process safe for solar panels?',
    answer: 'Yes! We use gentle, non-abrasive methods and pure water to protect your panels and warranty. Our technicians are trained in safe solar panel cleaning techniques.'
  },
  {
    question: 'Will cleaning my solar panels increase their output?',
    answer: {
      intro: 'Yes! Clean panels can significantly improve energy production by:',
      list: [
        'Removing dust and debris that blocks sunlight',
        'Eliminating bird droppings and other contaminants',
        'Preventing hot spots that can reduce efficiency',
        'Maintaining optimal light absorption'
      ],
      outro: 'Regular cleaning can improve your system\'s efficiency by up to 15-20%.'
    }
  }
];

const SolarPanelCleaning = () => {
  const serviceSEO = {
    title: 'Solar Panel Cleaning Services | Copper Canyon Pressure Wash',
    description: 'Professional solar panel cleaning in Phoenix, AZ. Remove dust & debris. Expert technicians, eco-friendly solutions. Get your free quote today!'
  };
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Solar Panel Cleaning',
    'provider': {
      '@type': 'LocalBusiness',
      'name': 'Copper Canyon Pressure Wash'
    },
    'areaServed': {
      '@type': 'City',
      'name': ['Phoenix', 'Scottsdale', 'Mesa', 'Tempe', 'Gilbert', 'Paradise Valley']
    },
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': 'Pressure Washing Services',
      'itemListElement': {
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': 'Solar Panel Cleaning'
        }
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <SEO 
        title="Solar Panel Cleaning Services | Copper Canyon Pressure Wash"
        description="Professional solar panel cleaning in Phoenix, AZ. Remove dust & debris. Expert technicians, eco-friendly solutions. Get your free quote today!"
        schema={serviceSchema}
        keywords="residential solar cleaning, commercial solar cleaning, Phoenix solar maintenance, solar efficiency cleaning, solar panel dust removal, solar system maintenance, solar panel safety cleaning, solar performance optimization, solar surface cleaning, solar energy maintenance"
      />
      <section className="w-full h-48 sm:h-64 md:h-80 lg:h-96 mb-8 rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center" aria-label="Solar Panel Cleaning Banner">
        <picture>
          <source srcSet="/solar_panels_hero.avif" type="image/avif" />
          <source srcSet="/solar_panels_hero.webp" type="image/webp" />
        <img 
          src="/solar_panels_hero.png" 
          alt="Professional solar panel cleaning in action" 
          className="w-full h-full object-cover object-center" 
            width="1600"
            height="400"
        />
        </picture>
      </section>
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl font-bold text-[#4a3f35] mb-6">Solar Panel Cleaning</h1>
        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4">Professional Solar Panel Cleaning Services</h2>
        <p className="mb-6 text-gray-700">
          At Copper Canyon Pressure Wash, we provide expert solar panel cleaning services for residential homes across the greater Phoenix area, from our base in Scottsdale. Arizona's dusty desert climate—combined with pollen, bird droppings, and hard water spots—can reduce your solar panels' efficiency by up to 20%. Our team uses specialized, low-pressure cleaning techniques and deionized water to safely clean your panels, ensuring maximum energy output and protecting your investment.
        </p>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Deionized Water</h3>
            </div>
            <p className="text-gray-700">We use deionized water to prevent mineral deposits, a common issue with Phoenix's hard water, keeping your panels spotless and efficient.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Low-Pressure Safety</h3>
            </div>
            <p className="text-gray-700">Our low-pressure washing at 500 PSI ensures a gentle clean that protects your solar panels from scratches or damage.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Performance Boost</h3>
            </div>
            <p className="text-gray-700">Regular cleaning can boost your panels' performance, saving you money on energy bills in Arizona's sunny climate.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Convenient Service</h3>
            </div>
            <p className="text-gray-700">We offer convenient, on-time solar panel cleaning services, tailored for homeowners in Arizona.</p>
          </div>
        </div>

        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4 mt-8">Our Solar Panel Cleaning Process</h2>
        <div className="bg-[#f8f7f3] rounded-lg p-6 mb-8">
          <div className="space-y-6">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">1</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Panel Inspection</h3>
                <p className="text-gray-700">We start by inspecting your solar panels for dirt, debris, or hard water stains that could affect their performance.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">2</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Solar-Safe Pre-Treatment</h3>
                <p className="text-gray-700">We use biodegradable, solar-safe detergents to loosen buildup without harming the panels' protective coatings.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">3</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Gentle Cleaning</h3>
                <p className="text-gray-700">With deionized water and low-pressure sprayers, we gently clean each panel, ensuring no streaks or spots remain.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">4</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Final Rinse & Check</h3>
                <p className="text-gray-700">We finish with a final rinse and check, leaving your panels optimized for energy production in the greater Phoenix area.</p>
              </div>
            </div>
          </div>
        </div>

        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4 mt-8">Our Expertise in Solar Panel Cleaning</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Pressure Control</h3>
            <p className="text-gray-700">We use the right pressure—never exceeding 500 PSI—to avoid cracking panels or voiding warranties, unlike high-pressure methods.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">All Panel Types</h3>
            <p className="text-gray-700">We're trained to clean all types of residential solar panels, including monocrystalline and polycrystalline, with care.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Damage Prevention</h3>
            <p className="text-gray-700">Our expertise prevents costly errors—like scratched coatings or damaged seals—that inexperienced cleaners might cause.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Best Practices</h3>
            <p className="text-gray-700">We stay updated on solar panel maintenance best practices, ensuring a safe, effective clean for Arizona's desert conditions.</p>
          </div>
        </div>
      </div>
      <div className="max-w-3xl mx-auto">
        <div className="bg-[#f8f7f3] border-l-4 border-[#b87c4c] p-6 rounded-lg shadow mb-8">
          <h3 className="text-lg font-bold text-[#b87c4c] mb-2">Get a Free Quote for Solar Panel Cleaning!</h3>
          <p className="mb-4 text-gray-700">Contact us today for a special offer on our solar panel cleaning services.</p>
          <Link 
            to="/contact" 
            className="inline-block px-6 py-2 bg-[#8B4513] text-white font-medium rounded-lg shadow hover:bg-[#6B3410] transition-colors duration-200"
          >
            Get a Free Quote
          </Link>
        </div>
      </div>
      <div className="max-w-2xl mx-auto mt-12 mb-12">
        <MiniContactForm />
      </div>
      <FAQ items={SOLAR_PANEL_FAQ} title="Frequently Asked Questions About Solar Panel Cleaning" />
    </div>
  );
};

export default SolarPanelCleaning; 